import { ChangeEvent, MouseEvent, useState } from 'react';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { Checkbox, Popover } from '@mui/material';
import {
	ClearFilterButton,
	DateFields,
	FilterIconButton,
	FilterLabel,
	FilterMenu,
	FilterOptionButton,
	FilterSearch,
	FilterSubLabel,
} from './styles';
import {
	TableDateRangeValue,
	TableFilter,
	TableFilterValue,
} from './types';

type TableFilterControlProps = {
	columnTitle: string;
	filter: TableFilter;
	value?: TableFilterValue;
	onChange: (value: TableFilterValue) => void;
};

export const isTableFilterActive = (value?: TableFilterValue) => {
	if (Array.isArray(value)) return value.length > 0;
	if (typeof value === 'string') return value.trim().length > 0;
	return Boolean(value?.preset || value?.from || value?.to);
};

const MultiSelectControl = ({
	columnTitle,
	filter,
	value,
	onChange,
}: TableFilterControlProps & { filter: Extract<TableFilter, { type: 'multi-select' }> }) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const [search, setSearch] = useState('');
	const selected = Array.isArray(value) ? value : [];
	const visibleOptions = filter.options.filter(({ label, secondary = '' }) => (
		`${label} ${secondary}`.toLowerCase().includes(search.toLowerCase())
	));
	const close = () => {
		setAnchor(null);
		setSearch('');
	};
	const toggle = (optionValue: string) => onChange(
		selected.includes(optionValue)
			? selected.filter((item) => item !== optionValue)
			: [...selected, optionValue],
	);
	const open = (event: MouseEvent<HTMLButtonElement>) => {
		filter.onOpen?.();
		setAnchor(event.currentTarget);
	};

	return (
		<>
			<FilterIconButton
				type="button"
				active={selected.length > 0}
				onClick={open}
				aria-label={`Filter ${columnTitle}`}
				aria-pressed={selected.length > 0}
			>
				<FilterAltOutlinedIcon />
			</FilterIconButton>
			<Popover
				open={Boolean(anchor)}
				anchorEl={anchor}
				onClose={close}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			>
				<FilterMenu>
					{filter.searchable && (
						<FilterSearch
							value={search}
							onChange={(event: ChangeEvent<HTMLInputElement>) => setSearch(event.target.value)}
							placeholder={filter.searchPlaceholder ?? `Search ${columnTitle.toLowerCase()}`}
							autoFocus
						/>
					)}
					{visibleOptions.map((option) => (
						<FilterOptionButton key={option.value} type="button" onClick={() => toggle(option.value)}>
							<Checkbox checked={selected.includes(option.value)} size="small" disableRipple />
							<span>
								<FilterLabel>{option.decoration ?? option.label}</FilterLabel>
								{option.secondary && <FilterSubLabel>{option.secondary}</FilterSubLabel>}
							</span>
						</FilterOptionButton>
					))}
					{!visibleOptions.length && <FilterSubLabel>No values</FilterSubLabel>}
					{selected.length > 0 && (
						<ClearFilterButton type="button" onClick={() => onChange([])}>
							Clear this filter
						</ClearFilterButton>
					)}
				</FilterMenu>
			</Popover>
		</>
	);
};

const DateRangeControl = ({
	columnTitle,
	filter,
	value,
	onChange,
}: TableFilterControlProps & { filter: Extract<TableFilter, { type: 'date-range' }> }) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const selected = (!Array.isArray(value) && typeof value === 'object' ? value : {}) as TableDateRangeValue;
	const presets = filter.presets ?? [{ value: 'all', label: 'All time' }];
	const inactivePreset = presets[0]?.value;
	const hasActiveValue = Boolean(
		(selected.preset && selected.preset !== inactivePreset) || selected.from || selected.to,
	);

	return (
		<>
			<FilterIconButton
				type="button"
				active={hasActiveValue}
				onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
				aria-label={`Filter ${columnTitle}`}
				aria-pressed={hasActiveValue}
			>
				<FilterAltOutlinedIcon />
			</FilterIconButton>
			<Popover
				open={Boolean(anchor)}
				anchorEl={anchor}
				onClose={() => setAnchor(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			>
				<FilterMenu>
					{presets.map((option) => (
						<FilterOptionButton
							key={option.value}
							type="button"
							onClick={() => onChange(option.value === inactivePreset
								? {}
								: { preset: option.value, from: '', to: '' })}
						>
							<Checkbox
								checked={(selected.preset ?? inactivePreset) === option.value && !selected.from && !selected.to}
								size="small"
								disableRipple
							/>
							<FilterLabel>{option.label}</FilterLabel>
						</FilterOptionButton>
					))}
					<DateFields>
						<label>
							<span>{filter.fromLabel ?? 'From'}</span>
							<input
								type="date"
								value={selected.from ?? ''}
								onChange={(event) => onChange({ preset: 'all', from: event.target.value, to: selected.to })}
							/>
						</label>
						<label>
							<span>{filter.toLabel ?? 'To'}</span>
							<input
								type="date"
								value={selected.to ?? ''}
								onChange={(event) => onChange({ preset: 'all', from: selected.from, to: event.target.value })}
							/>
						</label>
					</DateFields>
					{hasActiveValue && (
						<ClearFilterButton type="button" onClick={() => onChange({})}>
							Clear this filter
						</ClearFilterButton>
					)}
				</FilterMenu>
			</Popover>
		</>
	);
};

const TextControl = ({
	columnTitle,
	filter,
	value,
	onChange,
}: TableFilterControlProps & { filter: Extract<TableFilter, { type: 'text' }> }) => {
	const [anchor, setAnchor] = useState<HTMLElement | null>(null);
	const text = typeof value === 'string' ? value : '';
	return (
		<>
			<FilterIconButton
				type="button"
				active={Boolean(text)}
				onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
				aria-label={`Filter ${columnTitle}`}
				aria-pressed={Boolean(text)}
			>
				<FilterAltOutlinedIcon />
			</FilterIconButton>
			<Popover
				open={Boolean(anchor)}
				anchorEl={anchor}
				onClose={() => setAnchor(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
			>
				<FilterMenu>
					<FilterSearch
						value={text}
						onChange={(event: ChangeEvent<HTMLInputElement>) => onChange(event.target.value)}
						placeholder={filter.placeholder ?? `Filter ${columnTitle.toLowerCase()}…`}
						autoFocus
					/>
					{text && (
						<ClearFilterButton type="button" onClick={() => onChange('')}>
							Clear this filter
						</ClearFilterButton>
					)}
				</FilterMenu>
			</Popover>
		</>
	);
};

export const TableFilterControl = (props: TableFilterControlProps) => {
	if (props.filter.type === 'multi-select') {
		return <MultiSelectControl {...props} filter={props.filter} />;
	}
	if (props.filter.type === 'date-range') {
		return <DateRangeControl {...props} filter={props.filter} />;
	}
	return <TextControl {...props} filter={props.filter} />;
};
